import {
  Typography as MuiTypography,
  Divider as MuiDivider,
} from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";
import { z } from "zod";

import useQueryParams from "../../hooks/useQueryParams";
import FiltersForm from "../../components/FiltersForm";
import React, { useMemo } from "react";
import DepthHeatmapChartContainer from "../../components/charts/depthHeatmapChart/DepthHeatmapChartContainer";
import useDropdown from "../../hooks/useDropdown";
import RawTable from "../../components/RawTable";

/* ---------- styled helpers ---------- */

const Typography = styled(MuiTypography)(spacing);
const Divider = styled(MuiDivider)(spacing);

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;

const TODAY = new Date();
export function getWaterYearRange(date = TODAY) {
  const year = date.getFullYear();
  const startYear = year - 2;
  return {
    start_date: `${startYear}-10-01`,
    end_date: `${year}-09-30`,
  };
}
const { start_date, end_date } = getWaterYearRange();

/* --- static option lists --- */
const LOCATION_OPTIONS = [
  { label: "CCR1 – Cherry Creek Res. Station 1", value: "79" },
  { label: "CCR2 – Cherry Creek Res. Station 2", value: "80" },
  { label: "CCR3 – Cherry Creek Res. Station 3", value: "81" },
  { label: "CCR Composite – Cherry Creek Res.", value: "120" },
];

// const PARAMETER_OPTIONS = [
//   { label: "Dissolved Oxygen", value: "3" },
//   { label: "Temperature, Water", value: "8" },
//   { label: "pH", value: "7" },
//   { label: "Oxidation Reduction Potential (ORP)", value: "11" },
//   { label: "Total Nitrogen", value: "29" },
//   { label: "Nitrogen, Nitrate + Nitrite", value: "27" },
//   { label: "Nitrogen, Ammonia", value: "50" },
//   { label: "Total Phosphorus", value: "28" },
//   { label: "Soluble Reactive Phosphorus", value: "46" },
//   { label: "TN:TP Ratio", value: "100138" },
//   { label: "TDN:TDP Ratio", value: "100139" },
//   { label: "TIN:SRP Ratio", value: "100140" },
// ];

/* --- defaults + Zod schema --- */
const DEFAULT_SEARCH_PARAMS = {
  start_date,
  end_date,
  locationid: 80,
  parameterid: 29,
};

const searchParamsSchema = z.object({
  start_date: z.string().default(DEFAULT_SEARCH_PARAMS.start_date),
  end_date: z.string().default(DEFAULT_SEARCH_PARAMS.end_date),
  locationid: z.coerce.number().default(DEFAULT_SEARCH_PARAMS.locationid),
  parameterid: z.coerce.number().default(DEFAULT_SEARCH_PARAMS.parameterid),
});

/* --- heatmap endpoint --- */
const ENDPOINT = "depth-profile-graph-data";

export default function FieldDepthProfile() {
  // 1) parse & default the URL → params
  const { params, setParams } = useQueryParams(searchParamsSchema);

  // const locOptions = useDropdown("results-simple/download-dropdown-locs-active");
  const parOptions = useDropdown(
    `${ENDPOINT}/list-parameters-dropdown-depth-profile-active`
  );

  // 2) stamp in the live defaults into our config
  const FILTERS_CONFIG = useMemo(
    () => [
      {
        type: "date",
        param: "start_date",
        label: "Start Date",
        default: params.start_date,
      },
      {
        type: "date",
        param: "end_date",
        label: "End Date",
        default: params.end_date,
      },
      {
        type: "select",
        param: "locationid",
        label: "Surface-water location",
        default: String(params.locationid),
        options: LOCATION_OPTIONS,
      },
      {
        type: "auto",
        param: "parameterid",
        label: "Parameter",
        default: String(params.parameterid),
        options: parOptions.filter(({ value }) =>
          [
            20, 634, 638, 25, 24, 2, 42, 10, 6, 29, 45, 50, 1, 27, 16, 11, 3,
            680, 1892, 28, 46, 47, 652, 19, 30, 100139, 100140, 100138, 8, 53,
            9, 7, 1902,
          ].includes(+value)
        ),
      },
    ],
    [parOptions, params]
  );

  const COLUMNS = [
    { title: "Date", field: "rdate", type: "date" },
    { title: "Location", field: "location_name" },
    { title: "Parameter", field: "parameter" },
    { title: "Value", field: "rvalue_converted", type: "numeric" },
    { title: "Unit", field: "unit_name" },
    { title: "Depth", field: "depth_value", type: "numeric" },
  ];

  return (
    <>
      <Helmet title="Reservoir Depth Profile" />

      <header>
        <Typography variant="h3" component="h1" gutterBottom>
          Reservoir Depth Profile
        </Typography>
        <Divider my={4} />
      </header>

      <main>
        {/* ---------- About This Page (moved to top) ---------- */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Page
          </Typography>
          <Typography variant="body1" gutterBottom>
            This page explores how water quality changes with depth in the
            reservoir. Depth profiling helps identify patterns such as{" "}
            <strong>thermal stratification</strong>,{" "}
            <strong>oxygen depletion</strong>, or{" "}
            <strong>nutrient accumulation</strong> at different layers of the
            water column.
          </Typography>
          <Typography variant="body1" gutterBottom>
            The most common parameters analyzed by depth include{" "}
            <strong>temperature</strong>, <strong>dissolved oxygen</strong>,{" "}
            <strong>pH</strong>, and nutrients such as{" "}
            <strong>Total Nitrogen</strong> or{" "}
            <strong>Soluble Reactive Phosphorus</strong>. Select a station and
            parameter to view vertical profiles over time.
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Tip:</strong> As you adjust the filters, the chart and table
            update automatically. The page URL updates too, so you can{" "}
            <strong>bookmark</strong> or
            <strong> share a direct link</strong> to your current view.
          </Typography>
        </Section>

        {/* ---------- Filters ---------- */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Filters
          </Typography>
          <FiltersForm config={FILTERS_CONFIG} onChange={setParams} />
        </Section>

        {/* ---------- Graph ---------- */}
        <Section>
          <DepthHeatmapChartContainer
            start_date={params.start_date}
            end_date={params.end_date}
            locationid={params.locationid}
            parameterid={params.parameterid}
            height={400}
            title="Graph"
          />
        </Section>

        {/* ---------- About Graph ---------- */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Graph
          </Typography>
          <Typography variant="body1" gutterBottom>
            This heatmap shows <strong>vertical profiles</strong> of water
            quality over time. The x-axis represents{" "}
            <strong>sampling dates</strong>, and the y-axis shows the
            <strong> measured depth</strong> in meters.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Each cell’s color represents the measured{" "}
            <strong>concentration or value</strong> of the selected parameter at
            that depth and date. <strong>Lighter or darker shades</strong>{" "}
            correspond to lower or higher values, respectively, based on a color
            scale shown below the chart.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Hover over a cell to view the exact <strong>date</strong>,{" "}
            <strong>depth</strong>, and <strong>value</strong> in the tooltip.
            Gray cells indicate no data was collected for that depth and date.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Some parameters use a <strong>custom color scale</strong> to
            emphasize critical environmental thresholds. Others use a dynamic
            scale based on the data range.
          </Typography>
        </Section>

        {/* ---------- Map & Table ---------- */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Raw Data Table
          </Typography>

          <RawTable endPoint={ENDPOINT} params={params} columns={COLUMNS} />
        </Section>
      </main>
    </>
  );
}
